import React, { useContext, useCallback } from 'react'
import { TextInput } from 'react-native'
import { useField, useFormikContext } from 'formik'

import { styled, isWeb, composeRefs } from 'tamagui'
import type { SizeTokens } from 'tamagui'

import { FormFieldContext } from './shared'
// import { FormTextInputProps } from '../../components/Form/FormTextInput'

import './FormInput.css'

type SizeTokenValue = string | number

type SizeVariantContext = {
  tokens: {
    space: Record<string, SizeTokenValue>
  }
  font?: {
    size: Record<string, SizeTokenValue>
    lineHeight: Record<string, SizeTokenValue>
  }
}

const defaultStyles = {
  className: 'FormInput',

  size: '$true',
  fontFamily: '$body',
  borderWidth: 1,
  outlineWidth: 0,
  color: '$color',

  ...(isWeb
    ? {
        tabIndex: 0,
      }
    : {
        focusable: true,
      }),

  borderColor: '$borderColor',
  backgroundColor: '$background',

  // this fixes a flex bug where it overflows container
  minWidth: 0,

  hoverStyle: {
    borderColor: '$borderColorHover',
  },

  focusStyle: {
    borderColor: '$borderColorFocus',
  },
} as const

const StyledTextInput = styled(
  TextInput,
  {
    name: 'TextInput',
    fontWeight: '400',
    color: '$color',
    outlineWidth: 0,
    outlineStyle: 'none',
    borderWidth: 1,
    borderRadius: '$2',
    borderColor: '$borderColor',
    placeholderTextColor: '$colorMuted',

    variants: {
      unstyled: {
        false: defaultStyles,
      },

      size: {
        '...size': (size: SizeTokens, context: unknown) => {
          const { tokens, font } = context as SizeVariantContext
          const sizeKey = String(size)
          const paddingKey = sizeKey === '$true' ? '$2' : sizeKey

          return {
            fontSize: font?.size[sizeKey],
            lineHeight: font?.lineHeight[sizeKey],
            padding: tokens.space[paddingKey],
          }
        },
      },
    } as const,

    defaultVariants: {
      unstyled: false,
    } as const,
  },
  { isInput: true }
)

export const FormTextInput = StyledTextInput.styleable<{
  onChange?: (value: string) => void
  submitBehaviour?: 'submit-form'
}>(({ onChange, submitBehaviour, ...props }, ref) => {
  const { submitForm } = useFormikContext()
  const formFieldContext = useContext(FormFieldContext)
  const [field, , helpers] = useField<string>(formFieldContext.name)

  // On web, we rely on the native <form> submit event (Enter in an input triggers
  // implicit form submission). We must NOT pass onSubmitEditing on web because
  // react-native-web's TextInput calls e.preventDefault() when onSubmitEditing is
  // provided, which blocks the browser's native form submission.
  // On native, onSubmitEditing triggers form submission for single-line inputs.
  const onSubmitEditing = useCallback(() => {
    if (submitBehaviour === 'submit-form' || !props.multiline) {
      void submitForm()
    }
  }, [submitForm, submitBehaviour, props.multiline])

  // useEffect(() => {
  //   const element = formFieldContext.formElementRef
  //   .current as HTMLInputElement | null

  // if (element) {
  //   if (formFieldContext.name) {
  //     element.setAttribute('name', formFieldContext.name)
  //   }

  //   if (props.secureTextEntry && props.passwordRules) {
  //     element.setAttribute('passwordrules', props.passwordRules)
  //   }
  // }
  // }, [formFieldContext.name, props.secureTextEntry, props.passwordRules])

  return (
    <StyledTextInput
      {...props}
      className='FormInput'
      ref={composeRefs(ref, (inputRef: TextInput | null) => {
        if (!inputRef) {
          formFieldContext.formElementRef.current = null
          return
        }

        formFieldContext.formElementRef.current = inputRef
      })}
      value={field.value}
      // Don't pass onSubmitEditing on web — react-native-web's TextInput calls
      // e.stopPropagation() on ALL keyDown events and e.preventDefault() when
      // onSubmitEditing is provided, which blocks native <form> submission.
      // On web, the browser's implicit form submission handles Enter→submit.
      {...(!isWeb && { onSubmitEditing })}
      onChangeText={(value) => {
        let newValue = value
        if (props.inputMode === 'numeric') {
          newValue = value.replace(/[^\d]/g, '')
        } else if (props.inputMode === 'decimal') {
          newValue = value.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1')
        }

        void helpers.setTouched(true)
        void helpers.setValue(newValue)

        if (onChange) {
          onChange(newValue)
        }
      }}
    />
  )
})
